import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HardHat, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const { updatePassword } = useAuth();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      // Supabase places a temporary session on the URL. supabase-js reads it automatically
      // (detectSessionInUrl=true), then updateUser() sets the new password for that session.
      await updatePassword(password);
      toast.success("Password reset — please sign in.");
      nav("/login", { replace: true });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md p-6 sm:p-8 border border-border shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-navy flex items-center justify-center">
            <HardHat className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-bold text-xl text-slate-900">BuildTrack</div>
        </div>
        <div className="mb-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">New password</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Reset your password</h1>
          <p className="text-sm text-slate-500 mt-1">Choose a strong password (at least 8 characters).</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
          <div className="space-y-1.5">
            <Label htmlFor="new" className="text-xs font-semibold uppercase tracking-wider text-slate-600">New password</Label>
            <Input id="new" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="reset-password" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm password</Label>
            <Input id="confirm" type="password" minLength={8} required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" data-testid="reset-confirm" />
          </div>
          {error && (
            <div className="text-sm text-status-danger-text bg-status-danger-bg border border-status-danger-border rounded-md px-3 py-2" data-testid="reset-error">
              {error}
            </div>
          )}
          <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-12 bg-brandblue hover:bg-brandblue-hover text-white text-base font-semibold">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>

        <div className="text-sm text-slate-500 mt-6 text-center">
          <Link to="/login" className="text-brandblue font-semibold hover:underline">Back to sign in</Link>
        </div>
      </Card>
    </div>
  );
}
